import { Head, router } from '@inertiajs/react'
import { useState } from 'react'
import {
  Box,
  Container,
  Typography,
  CardContent,
  Grid,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  ThemeProvider
} from '@mui/material'
import CustomCard from '../../components/CustomCard'
import {
  Check as CheckIcon,
  Star as StarIcon
} from '@mui/icons-material'
import { Navigation, ListingOnboardingForm } from '../../components'
import theme from '../../styles/theme'
import balloons from '../../assets/ballons.svg'
import rocket from '../../assets/rocket.svg'
import PrimaryButton from '../../components/PrimaryButton'

export default function PricingPage() {
  const [showOnboarding, setShowOnboarding] = useState(false)

  const handleStartCreating = () => {
    setShowOnboarding(true)
    // Smooth scroll to the form
    setTimeout(() => {
      document.getElementById('onboarding-form')?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      })
    }, 100)
  }

  return (
    <ThemeProvider theme={theme}>
      <Head title="Pricing - HeyyProperty" />
      
      <Navigation />
      
      {/* Hero Section */}
      <Box sx={{ bgcolor: 'white', pt: 12, pb: 6 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', maxWidth: 600, mx: 'auto' }}>
            <Typography
              variant="h2"
              component="h1"
              sx={{
                fontWeight: 700,
                fontSize: { xs: '2rem', sm: '2.5rem' },
                color: 'grey.900',
                mb: 2
              }}
            >
              Simple, transparent pricing
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* For Renters Section */}
      <Box sx={{ py: 2, bgcolor: 'white' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography
              variant="h3"
              component="h2"
              sx={{
                fontWeight: 700,
                fontSize: { xs: '1.875rem', sm: '2.25rem' },
                color: 'grey.900',
                mb: 2
              }}
            >
              For Renters
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: 'grey.600',
                fontWeight: 400,
                fontSize: '1.125rem'
              }}
            >
              Find your perfect home with our AI-powered search
            </Typography>
          </Box>
          <Grid container spacing={4} justifyContent="center">
            <Grid item xs={12} md={6} lg={4}>
              <CustomCard
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  bgcolor: 'white'
                }}
              >
                {/* Black Banner Section */}
                <Box sx={{ bgcolor: 'grey.900', p: 3, textAlign: 'center' }}>
                  <Typography
                    variant="h4"
                    component="h2"
                    sx={{
                      fontWeight: 700,
                      color: 'white',
                      mb: 1
                    }}
                  >
                    Free
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      color: 'grey.300'
                    }}
                  >
                    Limited time
                  </Typography>
                </Box>

                <CardContent sx={{ p: 4, flex: 1, display: 'flex', flexDirection: 'column' }}>
                  <List sx={{ flex: 1, mb: 4 }}>
                    <ListItem sx={{ px: 0 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <CheckIcon sx={{ color: '#10b981', fontSize: 20 }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="AI-powered home search"
                        sx={{ '& .MuiListItemText-primary': { fontSize: '0.875rem' } }}
                      />
                    </ListItem>
                    <ListItem sx={{ px: 0 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <CheckIcon sx={{ color: '#10b981', fontSize: 20 }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Personalized recommendations"
                        sx={{ '& .MuiListItemText-primary': { fontSize: '0.875rem' } }}
                      />
                    </ListItem>
                    <ListItem sx={{ px: 0 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <CheckIcon sx={{ color: '#10b981', fontSize: 20 }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Easy tour scheduling"
                        sx={{ '& .MuiListItemText-primary': { fontSize: '0.875rem' } }}
                      />
                    </ListItem>
                    <ListItem sx={{ px: 0 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <CheckIcon sx={{ color: '#10b981', fontSize: 20 }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Pet-friendly search"
                        sx={{ '& .MuiListItemText-primary': { fontSize: '0.875rem' } }}
                      />
                    </ListItem>
                  </List>

                  <PrimaryButton
                    fullWidth
                    onClick={() => router.visit('/renters')}
                    sx={{
                      color: 'white',
                      py: 1.5,
                      fontSize: '1rem',
                      fontWeight: 600,
                      textTransform: 'none'
                    }}
                  >
                    Get Started
                  </PrimaryButton>
                </CardContent>
              </CustomCard>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* For Landlords Section */}
      <Box sx={{ py: 6, bgcolor: 'grey.50' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography
              variant="h3"
              component="h2"
              sx={{
                fontWeight: 700,
                fontSize: { xs: '1.875rem', sm: '2.25rem' },
                color: 'grey.900',
                mb: 2
              }}
            >
              For Landlords
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: 'grey.600',
                fontWeight: 400,
                fontSize: '1.125rem'
              }}
            >
              Streamline your property management and find quality tenants
            </Typography>
          </Box>
          <Grid container spacing={4} justifyContent="center">
            {/* Free Plan */}
            <Grid item xs={12} md={6} lg={4}>
              <CustomCard
                sx={{ 
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  bgcolor: 'white'
                }}
              >
                {/* Black Banner Section */}
                <Box sx={{ bgcolor: 'grey.900', p: 3, textAlign: 'center' }}>
                  <Typography
                    variant="h4"
                    component="h2"
                    sx={{
                      fontWeight: 700,
                      color: 'white',
                      mb: 1
                    }}
                  >
                    Free{' '}
                    <Box
                      component="span"
                      sx={{
                        fontSize: '0.6em',
                        fontWeight: 400,
                        opacity: 0.8
                      }}
                    >
                      (Limited Time)
                    </Box>
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      color: 'grey.300'
                    }}
                  >
                    Perfect for getting started
                  </Typography>
                </Box>
                
                <CardContent sx={{ p: 4, flex: 1, display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                    <img 
                      src={balloons} 
                      alt="Free plan illustration"
                      style={{
                        width: '80px',
                        height: '80px',
                        objectFit: 'contain'
                      }}
                    />
                  </Box>

                  <List sx={{ flex: 1, mb: 4 }}>
                    <ListItem sx={{ px: 0 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <CheckIcon sx={{ color: '#10b981', fontSize: 20 }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Smart prospect screening"
                        sx={{ '& .MuiListItemText-primary': { fontSize: '0.875rem' } }}
                      />
                    </ListItem>
                    <ListItem sx={{ px: 0 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <CheckIcon sx={{ color: '#10b981', fontSize: 20 }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Tour scheduling"
                        sx={{ '& .MuiListItemText-primary': { fontSize: '0.875rem' } }}
                      />
                    </ListItem>
                    <ListItem sx={{ px: 0 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <CheckIcon sx={{ color: '#10b981', fontSize: 20 }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Awesome listing descriptions"
                        sx={{ '& .MuiListItemText-primary': { fontSize: '0.875rem' } }}
                      />
                    </ListItem>
                    <ListItem sx={{ px: 0 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <CheckIcon sx={{ color: '#10b981', fontSize: 20 }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Show off your popularity"
                        sx={{ '& .MuiListItemText-primary': { fontSize: '0.875rem' } }}
                      />
                    </ListItem>
                    <ListItem sx={{ px: 0 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <CheckIcon sx={{ color: '#10b981', fontSize: 20 }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="All tours in one place"
                        sx={{ '& .MuiListItemText-primary': { fontSize: '0.875rem' } }}
                      />
                    </ListItem>
                    <ListItem sx={{ px: 0 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <CheckIcon sx={{ color: '#10b981', fontSize: 20 }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="More to come"
                        sx={{ '& .MuiListItemText-primary': { fontSize: '0.875rem' } }}
                      />
                    </ListItem>
                  </List>

                  <Button
                    variant="contained"
                    fullWidth
                    onClick={handleStartCreating}
                    sx={{
                      bgcolor: 'grey.900',
                      color: 'white',
                      py: 1.5,
                      fontSize: '1rem',
                      fontWeight: 600,
                      textTransform: 'none',
                      '&:hover': {
                        bgcolor: 'grey.900',
                        color: 'white'
                      }
                    }}
                  >
                    Get Started Free
                  </Button>
                </CardContent>
              </CustomCard>
            </Grid>

            {/* Premium Features Coming Soon Card */}
            <Grid item xs={12} md={6} lg={4}>
              <CustomCard
                sx={{ 
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  bgcolor: 'white',
                  border: '2px solid #fbbf24'
                }}
              >
                {/* Premium Banner Section */}
                <Box sx={{ bgcolor: '#fbbf24', p: 3, textAlign: 'center' }}>
                  <Typography
                    variant="h4"
                    component="h2"
                    sx={{
                      fontWeight: 700,
                      color: 'white',
                      mb: 1
                    }}
                  >
                    Premium
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      color: 'white',
                      opacity: 0.9
                    }}
                  >
                    Coming Soon
                  </Typography>
                </Box>
                
                <CardContent sx={{ p: 4, flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                  <Box sx={{ textAlign: 'center', mb: 3 }}>
                    <img 
                      src={rocket} 
                      alt="Premium features coming soon"
                      style={{
                        width: '80px',
                        height: '80px',
                        objectFit: 'contain'
                      }}
                    />
                  </Box>

                  <Typography
                    variant="h6"
                    sx={{
                      textAlign: 'center',
                      color: 'grey.600',
                      mb: 3,
                      fontWeight: 500
                    }}
                  >
                    Advanced features for power users
                  </Typography>

                  <List sx={{ flex: 1, mb: 4 }}>
                    <ListItem sx={{ px: 0 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <StarIcon sx={{ color: '#fbbf24', fontSize: 20 }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Advanced analytics"
                        sx={{ '& .MuiListItemText-primary': { fontSize: '0.875rem', color: 'grey.600' } }}
                      />
                    </ListItem>
                    <ListItem sx={{ px: 0 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <StarIcon sx={{ color: '#fbbf24', fontSize: 20 }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Priority support"
                        sx={{ '& .MuiListItemText-primary': { fontSize: '0.875rem', color: 'grey.600' } }}
                      />
                    </ListItem>
                    <ListItem sx={{ px: 0 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <StarIcon sx={{ color: '#fbbf24', fontSize: 20 }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Custom integrations"
                        sx={{ '& .MuiListItemText-primary': { fontSize: '0.875rem', color: 'grey.600' } }}
                      />
                    </ListItem>
                    <ListItem sx={{ px: 0 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <StarIcon sx={{ color: '#fbbf24', fontSize: 20 }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="And much more..."
                        sx={{ '& .MuiListItemText-primary': { fontSize: '0.875rem', color: 'grey.600' } }}
                      />
                    </ListItem>
                  </List>

                  <Button
                    variant="outlined"
                    fullWidth
                    disabled
                    sx={{
                      borderColor: '#fbbf24',
                      color: '#fbbf24',
                      py: 1.5,
                      fontSize: '1rem',
                      fontWeight: 600,
                      textTransform: 'none',
                      '&:disabled': {
                        borderColor: '#fbbf24',
                        color: '#fbbf24',
                        opacity: 0.7
                      }
                    }}
                  >
                    Coming Soon
                  </Button>
                </CardContent>
              </CustomCard>
            </Grid>
          </Grid>
        </Container>
      </Box>
      <ListingOnboardingForm isVisible={showOnboarding} />
    </ThemeProvider>
  )
}
